/**
 * OverviewTab — Sprint-16 nach Mockup 4c.
 *
 * Header: Eyebrow „— ÜBERSICHT" + Serif-Word-Split „Auf einen *Blick*."
 * Body: 2x2 Card-Grid (STAMMDATEN, BELEGUNG · 14 TAGE, CHANNELS, PREISE-navy).
 *
 * Property-Name + Meta-Row wandert in den Parent (ApartmentDetailLayout).
 */
import { useQuery } from '@tanstack/react-query';
import { PRIVILEGES } from '@stayon/auth-core';
import { Eyebrow } from '@/shared/components';
import { channelIsActive, useApi } from '@/shared/api';
import { can, isWorkspaceAdmin, useAuth } from '@/shared/auth';
import { ObjectPublicationBanner } from '../ObjectPublicationBanner';
import { BelegungTile } from '../overview-bento/BelegungTile';
import { ChannelsTile } from '../overview-bento/ChannelsTile';
import { PreiseTile } from '../overview-bento/PreiseTile';
import { StammdatenTile } from '../overview-bento/StammdatenTile';
import { useApartmentDetail } from './ApartmentDetailLayout';
import { ArchivedPropertySnapshot } from './ArchivedPropertySnapshot';
import { propertyKeys } from '../queries';

export function OverviewTab() {
  const { propertyId: id, readOnly } = useApartmentDetail();
  const api = useApi();
  const { user } = useAuth();

  const { data: property } = useQuery({
    queryKey: propertyKeys.detail(id),
    queryFn: () => api.properties.get(id),
  });

  const stripFrom = new Date().toISOString().slice(0, 10);
  const stripToDate = new Date();
  stripToDate.setUTCDate(stripToDate.getUTCDate() + 13);
  const stripTo = stripToDate.toISOString().slice(0, 10);
  const { data: calendar } = useQuery({
    queryKey: ['calendar', 'strip', stripFrom, stripTo],
    queryFn: () => api.calendar.getRange(stripFrom, stripTo),
    staleTime: 60_000,
    enabled: !readOnly,
  });
  const myCalendarRow = calendar?.properties.find((r) => r.propertyId === id);
  const bookedDays = myCalendarRow?.cells.filter((c) => c.status === 'booked').length ?? 0;
  const occupancyPct = myCalendarRow ? Math.round((bookedDays / 14) * 100) : null;

  const { data: channelsData } = useQuery({
    queryKey: ['channex', 'channels'],
    queryFn: () => api.channex.listChannels(),
    staleTime: 60_000,
    // Channex-Channel-Liste ist WorkspaceAdmin-only → für andere Rollen nicht abfragen
    // (403-Rauschen auf der Owner/Reinigung-Landing); ChannelsTile degradiert dann sauber.
    enabled: !readOnly && !!property?.channexUnitPublished && isWorkspaceAdmin(user),
  });
  const mappedChannels =
    channelsData?.data.filter((c) =>
      c.attributes.properties?.includes(property?.channexPropertyId ?? ''),
    ) ?? [];
  const liveChannels = mappedChannels.filter(channelIsActive);

  if (!property) return null;
  if (readOnly) return <ArchivedPropertySnapshot property={property} />;

  return (
    <>
      <header className="mb-6">
        <Eyebrow>Übersicht</Eyebrow>
        <h2 className="text-ink mt-3 font-serif text-3xl font-normal tracking-tight">
          Auf einen <span className="font-serif-italic">Blick</span>
          <span className="text-sealwax">.</span>
        </h2>
      </header>

      <ObjectPublicationBanner property={property} />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:grid-rows-2">
        <StammdatenTile property={property} />
        <BelegungTile
          propertyId={id}
          cells={myCalendarRow?.cells}
          stripFrom={stripFrom}
          occupancyPct={occupancyPct}
          bookedDays={bookedDays}
        />
        <ChannelsTile
          property={property}
          mappedChannels={mappedChannels}
          liveChannels={liveChannels}
        />
        {can(user, PRIVILEGES.availabilityDistributionView) && <PreiseTile property={property} />}
      </div>

      {property.description && (
        <div className="bg-card border-line/50 shadow-card mt-4 rounded-lg p-6">
          <Eyebrow variant="muted" className="mb-3">
            Notizen · intern
          </Eyebrow>
          <p className="text-ink-soft whitespace-pre-wrap text-sm leading-relaxed">
            {property.description}
          </p>
        </div>
      )}
    </>
  );
}
